/**
 * @date: 12/22/20
 * @author: Yan Wang <ywang@max.md>
 * @copy: (C) Copyright 2020 MaxMD Corporation, All Rights Reserved.
 *
 */

import React, {useState} from "react";
import {Button, Container, alpha} from "@mui/material";
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import ShowMessage from "../utils/show-message";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import makeStyles from '@mui/styles/makeStyles';
import {getQueryParams, handleHttpResponseAcceptAuthError} from "../utils/http-helpers";

const useStyles = makeStyles((theme) => ({
    formRoot: {
        width: '100%',
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
            width: '25ch',
        },
    },
    grow: {
        flexGrow: 1,
    },
    menuButton: {
        marginRight: theme.spacing(2),
    },
    title: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block',
        },
    },
    search: {
        position: 'relative',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: alpha(theme.palette.common.white, 0.15),
        '&:hover': {
            backgroundColor: alpha(theme.palette.common.white, 0.25),
        },
        marginRight: theme.spacing(2),
        marginLeft: 0,
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            marginLeft: theme.spacing(3),
            width: 'auto',
        },
    },
    searchIcon: {
        padding: theme.spacing(0, 2),
        height: '100%',
        position: 'absolute',
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    inputRoot: {
        color: 'inherit',
    },
    inputInput: {
        padding: theme.spacing(1, 1, 1, 0),
        // vertical padding + font size from searchIcon
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('md')]: {
            width: '20ch',
        },
    },
    sectionDesktop: {
        display: 'none',
        [theme.breakpoints.up('md')]: {
            display: 'flex',
        },
    },
    sectionMobile: {
        display: 'flex',
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 350,
    },
    formInput: {
        margin: theme.spacing(1),
        minWidth: '100%',
    },
    loginButton: {
        minHeight: 56,
        width: 300,
        // minWidth: '100%',
        fontSize: 17,
        fontWeight: 'bold',
        textTransform: 'none',
        marginBottom: 10
    },
    forgotPwdButton: {
        textTransform: 'none'
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff'
    },
    alignLeft: {textAlign: "left"}
}));

export default function ForgotPassword(props) {
    const classes = useStyles();
    const queryParams = getQueryParams();
    const backUrl = queryParams.back || "";
    const [msg, setMsg] = useState({});
    const [loginLoading, setLoginLoading] = useState(null);
    const [form, setForm] = useState({});

    const handleFormFieldChanged = (name, value) => {
        const newParams = {...form};
        newParams[name] = value;
        setForm(newParams);
    }
    const handleFormKeypress = (e) => {
        if (e.key === "Enter"){
            handleSubmitForm()
        }
    }
    const handleSubmitForm = () => {

        if (!form.username){
            setMsg({warning: `Please enter the username`});
            return false;
        }
        setMsg({});

        setLoginLoading('Loading');
        const url = `/auth/basic/forgotPassword`;
        const requestOption = {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(form)
        };
        console.log(`POST ${url}`, requestOption);

        fetch(url, requestOption)
            .then(handleHttpResponseAcceptAuthError)
            .catch( (err) => {
                console.error('send forgot-pwd linnk failed', err);
                setLoginLoading(null);
                if (err){
                    setMsg({warning: `Failed to send forgot-password link: ${err}`});
                }
                return false;
            }).then ( (res) => {
                console.log(res);
                setLoginLoading(null);
                if (res){
                    if (res.succ){
                        setMsg({succ: res.message});
                    } else {
                        setMsg({warning: res.message});
                    }
                }
            });
    }

    return (
        <Container className='auth-container' maxWidth='xs' >
            <div className='logo-wrapper'>
                <img src={require('../images/dt-logo.png')?.default}/>
            </div>
            {/*<br/>*/}
            {/*<img src={require('../images/dt-logo.png')}/>*/}
            {/*<br/><br/>*/}

            <h3 className='auth-title'>Reset Your Password</h3>

            {queryParams && queryParams.error &&
            <Alert severity="warning">
                <AlertTitle> <strong>{queryParams.error} {queryParams.errorTitle || ""}</strong> </AlertTitle>
                {queryParams.errorDescription || ""}
            </Alert>
            }

            <form className={classes.formRoot} noValidate autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                <ShowMessage msg={msg} />
                <div>
                    <TextField className={classes.formInput}
                               label="Enter Username" type='text'
                               onKeyPress={handleFormKeypress} onChange={(e) => handleFormFieldChanged('username',e.target.value) } />
                </div>
                <br/>
                <div>
                    <Button className={classes.loginButton} size='large' variant='contained' color='primary' onClick={handleSubmitForm} disabled={Boolean(loginLoading)} >
                        {loginLoading ? <><CircularProgress color={"inherit"} /> {loginLoading} ...</> : <>Submit</>}
                    </Button>
                </div>
                <div>
                    {backUrl && <Button className={classes.forgotPwdButton} size='large' variant='text' color='primary' href={backUrl} target='_self' >Back</Button>}

                </div>
            </form>

        </Container>

    );
}
